import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import FileSaver from "file-saver";
import { isEmpty, noop } from "lodash";
import { observer } from "mobx-react-lite";
import "swiper/css";
import "swiper/css/pagination";
import { useEffectOnce, useForceUpdate } from "@lib-shared/hooks";
import { CachedItem, removeEmptyEntries } from "@lib-shared/lib";
import { appSettings } from "core/config";
import { BusinessPage, Key, MenuArea, Messages } from "core/constants";
import { AppContext } from "core/context";
import { SelfServiceContext } from "core/context/self-service.context";
import getActiveFeatures from "core/features/get-active-features";
import { sessionManager } from "core/instances";
import { dateWrapper, logApiError } from "core/lib";
import { getContactDetailsHtml } from "core/lib/html-helpers";
import { updateProfileAsync } from "core/lib/update-profile";
import UrlUtils from "core/lib/url-utils";
import User, { getUser } from "core/models/user";
import CaseManagementApi from "core/services/case-management.service";
import { StatementService } from "core/services/cef";
import UmbracoService from "core/services/umbraco.service";
import { UserActivityLogger } from "core/services/user-tracking-helper";
import ActionButtons from "./components/action-buttons";
import AmountToPayBox from "./components/amount-to-pay-box";
import { ModalAlert, PleaseWait } from "components/common-ui";
import FixError from "./components/fix-error";
import Subscriptions from "./components/subscriptions";

//TODO: Clean/split up
const Dashboard = observer(() => {
    const appContext = useContext(AppContext);
    const selfServiceContext = useContext(SelfServiceContext);
    const [openModal, setOpenModal] = useState(false);

    const navigate = useNavigate();

    const forceUpdate = useForceUpdate();

    const stores = selfServiceContext.stores;

    const clearErrorStore = stores.clearError;

    const [contactDetailsHtml, setContactDetailsHtml] = useState("");
    const contactDetailsCache = new CachedItem(Key.contactDetails);

    const numTiles = 5;
    const maxSwiperWidth = (numTiles * 181) + ((numTiles - 1) * 15);

    const show = getActiveFeatures();

    const onPopulate = {
        error: () => {
            noop();
            //forceUpdate();
        }
    };

    if (!User.instance) {
        getUser();
    }

    const user = appContext.user as User;

    const getRegionContactDetails = async () => {
        if (contactDetailsCache.isCached) {
            return contactDetailsCache.value;
        }

        const umbracoApi = new UmbracoService();
        const emailApi = new CaseManagementApi();

        try {
            const contactDetails = (await umbracoApi.getContactDetails()).data;
            const email = (await emailApi.getEmail(user.countryCode)).data;

            contactDetailsCache.set({ ...contactDetails, ...email });
            setContactDetailsHtml(getContactDetailsHtml());
        }
        catch (error: any) {
            logApiError(error);

            console.error("Error occurred retrieving Contact Details from Umbraco Api:", error.response);
        }
    }

    const checkUserHasNames = async () => {
        if (!appSettings.isEnabled.forceUserNames) {
            return true;
        }

        const { firstName, lastName } = sessionManager.getNameOfUser();

        if (!firstName || !lastName) {
            console.log("User does not have first or last name set");
            navigate(BusinessPage.setName);
            return false;
        }

        if (!User.instance) {
            getUser();
        }

        return true;
    }

    const getSwiperWidth = () => {
        const clientWidth = document.body.clientWidth;
        return Math.min(maxSwiperWidth, clientWidth - 60);
    }

    const clearPayments = () => {
        console.log("Clearing amount to pay");
        selfServiceContext.stores.amountToPay.reset();
    }

    const onLoad = async () => {
        appContext.updateTitle("Dashboard");
        appContext.updateHeader("");

        UrlUtils.removeQueryParamsFromUrl();

        if (!checkUserHasNames()) {
            return;
        }

        await getRegionContactDetails();

        new UserActivityLogger("Dashboard").read();

        await selfServiceContext.managers.populateAll(onPopulate);

        forceUpdate();
    }

    const retryAmountToPay = async () => {
        await selfServiceContext.managers.amountToPay.populateAsync(onPopulate);
    }

    const detailsStore = stores.businessDetails;

    const FixErrorBox = () => {
        return <>
            <h5>Do you have an error on one<br />of your TVs?</h5>

            <button className="btn btn-primary" onClick={displayClearErrorModal}>
                Fix Error
            </button>
        </>;
    }

    const downloadStatement = (item: any) => {
        const api = new StatementService();

        if (!show.downloadStatements) {
            ModalAlert.error(Messages.statementsUnavailable);
            return;
        }

        const documentId = item.documentId;

        ModalAlert.alert("Download", "Your statement will download shortly");

        const triggerDownload = (response: any) => {
            if (response.status !== 200) {
                return;
            }

            const blob = new Blob([response.data], {
                type: response.headers["content-type"],
            });

            FileSaver.saveAs(blob, `${documentId}.pdf`);
        };

        api.downloadStatement(documentId)
            .then(triggerDownload)
            .catch((error: any) => {
                const message = "Error downloading statement";
                ModalAlert.error(message);
                console.log(message, error);
            });
    };

    const StatementsBox = () => {
        const statement = stores.latestStatement.latestStatement;

        if (!statement) {
            return null;
        }

        console.log(statement);

        const date = dateWrapper(statement.data.from).fullMonthAndYear;

        return (
            <>
                <h5>Statements and Invoices</h5>

                <span className="download-statement" onClick={() => downloadStatement(statement)}>
                    {date}

                    <i className="icon icon-downloads" />
                </span>

                <button className="btn btn-primary" onClick={() => navigate(BusinessPage.statementsAndInvoices)}>
                    View More Statements
                </button>
            </>
        );
    }

    const UsersBox = () => {
        return (
            <>
                <h5>Do you want to add a user?</h5>

                <button className="btn btn-primary" onClick={() => navigate(BusinessPage.users)}>
                    Add New User
                </button>
            </>
        );
    }

    const MainAction = () => {
        // const setTestMode = (mode: string) => {
        //     show.pay = mode === "p";
        //     show.fixError = mode === "f";
        //     show.statements = mode === "s";
        //     show.users = mode === "u";
        // }

        // setTestMode("s");

        if (show.pay) {
            return <AmountToPayBox store={stores.amountToPay} clearPayments={clearPayments} retryAmountToPay={retryAmountToPay} />;
        }

        if (show.fixError) {
            return <FixErrorBox />
        }

        if (show.statements) {
            return <StatementsBox />
        }

        if (show.users) {
            return <UsersBox />
        }

        return null;
    }

    const Title = () => {
        const name = user.fullName ?? "";

        return (<h1>{name}</h1>);
    }

    useEffectOnce(() => {
        appContext.updateArea(MenuArea.dashboard);

        onLoad();
    });

    const detailsLine = (label: string, value: string | undefined) => {
        if (isEmpty(value)) {
            return null;
        }

        return (<p>
            <label>{label}</label>
            {value}
        </p>);
    }

    const showLoading = !detailsStore.isPopulated || detailsStore.isLoading || !user?.company;

    if (detailsStore.hasError && !detailsStore.isLoading) {
        ModalAlert.error("Sorry, there has been an error getting your account details", "Error", {
            id: "dashboard-error",
            onClose: () => {
                detailsStore.reset();
                ModalAlert.closeAll();
                selfServiceContext.populateAll(onPopulate);
            },
            buttonText: "Retry"
        });

        return null;
    }

    if (showLoading) {
        return <PleaseWait message="Getting account details" />;
    }

    const getAddress = () => {
        const businessDetails = detailsStore.businessDetails;

        if (!businessDetails) {
            return "";
        }

        const address = businessDetails.address;
        let lines = [address?.city, address?.suburb, address?.province];
        lines = removeEmptyEntries(lines) as Array<string>;
        lines = lines.map((x): string => x?.toTitleCase() ?? "");

        return lines.join(", ");
    };

    const address = getAddress();

    const displayClearErrorModal = () => {
        setOpenModal(true);
        forceUpdate();
    }

    const BusinessDetails = () => {
        const details = detailsStore.businessDetails;

        if (!detailsStore.isPopulated || !details) {
            return null;
        }

        return <div className="box business-details">
            <h3>{user.businessName}</h3>
            <address>
                {address}
            </address>

            {detailsLine("Customer Number", user.company?.customerNumber.toString())}
            {detailsLine("VAT Number", details.vatNumber)}
            {detailsLine("Registration Number", details.registrationNumber)}
        </div>;
    }

    const KeyContactDetails = () => {
        const details = detailsStore.businessDetails;

        if (!detailsStore.isPopulated || !details) {
            return null;
        }

        return <div className="box key-contact">
            <h3>{detailsStore.businessDetails?.contactName}</h3>

            {detailsLine("Role", details.role)}
            {detailsLine("Email", details.email)}
            {detailsLine("Phone", details.phone ?? "")}
        </div>;
    }

    const swiperWidth = getSwiperWidth();

    return (<>
        <div className="dashboard">
            <section className="title">
                <Title />
            </section>

            <section className="primary">
                <div className="box primary-action">
                    <div className="primary-action-container">
                        <MainAction />
                    </div>
                </div>
            </section>

            <section className="left">
                <BusinessDetails />

                <KeyContactDetails />

                <div className="box primary-action">
                    <MainAction />
                </div>
            </section>

            <section className="top">
                <h2>What would you like to do?</h2>

                <ActionButtons
                    width={swiperWidth}
                    displayClearErrorModal={displayClearErrorModal} />
            </section>

            <section className="main">
                <Subscriptions />
            </section>

            <FixError
                openModal={openModal}
                onClose={() => setOpenModal(false)}
                contactDetailsHtml={contactDetailsHtml}
                store={clearErrorStore} />
        </div>

        {/* <div className="debug">
            <h1 className="is-desktop">Desktop</h1>
            <h1 className="not-desktop">Not Desktop</h1>
            <h1 className="is-tablet">Tablet</h1>
            <h1 className="is-phone">Phone</h1>
        </div> */}
    </>
    )
});

export default Dashboard;
